'use client';
import { useTranslations } from 'next-intl';
import { Fragment, useMemo, useState } from 'react';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { Fail } from '@/app/components/icons/Fail';
import { ProductFillFrames } from '@/app/components/icons/ProductFillFrames';
import { ProductFillMultiagent } from '@/app/components/icons/ProductFillMultiagent';
import { ProductLineAiVideo } from '@/app/components/icons/ProductLineAiVideo';
import { ProductLineElement } from '@/app/components/icons/ProductLineElement';
import { ProductLineVideoReference } from '@/app/components/icons/ProductLineVideoReference';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { MediaPromptPreview } from '@/app/components/shared/MediaPromptPreview';
import { MediaWithMaskBg } from '@/app/components/shared/MediaWithMaskBg';
import {
  ReferenceAudioPreviewDialog,
  type ReferenceAudioPreviewInfo,
} from '@/app/components/shared/ReferenceAudioPreviewDialog';
import { ReferenceCard } from '@/app/components/shared/ReferenceCard';
import { TextElementCover } from '@/app/components/shared/TextElementCover';
import { useStore } from '@/app/providers/store';
import type { ElementType, ImagePrompt, TaskInfo, TaskInputPrompts, VideoPrompt } from '@/app/types';
import { cn, convertViduV1ElementToElementType } from '@/app/utils';

const MAX_DISPLAY_ITEMS = 3;

interface MediaPromptsDisplayProps {
  prompts: TaskInputPrompts[];
  taskType?: TaskInfo['type'];
  className?: string;
  thumbnailClassName?: string;
  collisionBoundary?: HTMLElement | null;
  collisionLeft?: number;
  /**
   * 是否禁用主体详情查看
   * 当为 true 时，主体卡片不可点击，无法查看详情
   */
  disableElementDetail?: boolean;
  /** 是否允许音频在详情浮层中播放 */
  enableAudioPlayback?: boolean;
  /** 是否允许点击音频资源卡打开音频预览弹窗 */
  enableAudioPreviewDialog?: boolean;
}

type ProcessedMedia =
  | { type: 'single'; src: string; name: string; audit_violation?: boolean }
  | { type: 'reference_single_img'; src: string; name: string; audit_violation?: boolean }
  | { type: 'video'; src: string; name: string; audit_violation?: boolean; cover?: string }
  | { type: 'audio'; src: string; name: string; audit_violation?: boolean }
  | {
      type: 'reference';
      name: string;
      material?: ElementType;
      prompt?: ImagePrompt | VideoPrompt | null;
      audit_violation?: boolean;
    }
  | { type: 'reference_text'; name: string; material?: ElementType; audit_violation?: boolean };

const useProcessedMedia = (prompts: TaskInputPrompts[], taskType?: TaskInfo['type']) => {
  return useMemo(() => {
    const media: ProcessedMedia[] = [];
    for (const prompt of prompts || []) {
      const audit_violation = prompt.audit?.audit_violation === true;
      if (prompt.type === 'image') {
        media.push({
          type: ['character2video', 'reference2image'].includes(taskType || '') ? 'reference_single_img' : 'single',
          src: prompt.content,
          name: prompt.name || '',
          audit_violation,
        });
      } else if (prompt.type === 'video') {
        media.push({
          type: 'video',
          src: prompt.content,
          name: prompt.name || '',
          audit_violation,
          cover: prompt.cover_uri,
        });
      } else if (prompt.type === 'audio') {
        media.push({
          type: 'audio',
          src: prompt.content,
          name: prompt.name || '',
          audit_violation,
        });
      } else if (prompt.type === 'material') {
        const material = prompt.material
          ? convertViduV1ElementToElementType({
              ...prompt.material,
              name: prompt.name || '',
              components: (prompt.material?.contents ?? []).map((item, index) => ({
                content: item,
                type: index === 0 ? 'main' : 'auxiliary',
                content_type: prompt.material?.modality === 'video' ? 'video' : 'image', // TODO: modality 为 video 时， component 的 content_type 暂时只能为 video（视频主体暂时不支持传图片）
                cover_uri: prompt.material?.modality === 'video' ? prompt.cover_uri : undefined,
              })),
              ...(!!prompt.material?.share_id &&
                prompt.material?.share_id !== '0' && {
                  share_meta: {
                    id: prompt.material?.share_id,
                    status: 'approved',
                  },
                }),
            })
          : undefined;

        const modality = material?.modality;

        // 根据 modality 处理不同类型的主体
        if (modality === 'text' && material) {
          // 文字主体：只显示文字内容
          media.push({
            type: 'reference_text',
            material,
            name: prompt.name || '',
            audit_violation,
          });
        } else if (material) {
          // 图片和视频主体：统一使用 optionalAssets 中的第一个作为预览
          const optionalAssets = material.optionalAssets;
          const firstPrompt = optionalAssets?.[0] as ImagePrompt | VideoPrompt | null;
          media.push({
            type: 'reference',
            material,
            prompt: firstPrompt ?? null,
            name: prompt.name || '',
            audit_violation,
          });
        }
      }
    }
    return media;
  }, [prompts, taskType]);
};

/**
 * Tooltip 中的视频组件，hover 时播放，隐藏 controls
 */
const TooltipVideo: React.FC<{ src: string; cover?: string }> = ({ src, cover }) => {
  return (
    <div className="h-full w-full overflow-hidden rounded-8">
      <MediaWithMaskBg
        src={src}
        maskSrc={cover}
        videoCoverImage={cover}
        className="h-full w-full"
        type="video"
        disableDrag={true}
      />
    </div>
  );
};

/**
 * 缩略图中的视频组件，静态显示，不播放
 */
const ThumbnailVideo: React.FC<{ src: string; className?: string; cover?: string }> = ({ src, className, cover }) => {
  return (
    <div className={cn('relative h-full w-full overflow-hidden rounded-2', className)}>
      <MediaWithMaskBg
        src={cover || src}
        maskSrc={cover}
        videoCoverImage={cover}
        className="h-full w-full"
        type={cover ? 'image' : 'video'}
        disableDrag={true}
        videoAutoPlay={false}
      />
      <ProductLineAiVideo className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 size-5 text-system-white" />
    </div>
  );
};

export const MediaPromptsDisplay: React.FC<MediaPromptsDisplayProps> = ({
  prompts,
  taskType,
  className,
  thumbnailClassName = 'size-10 shrink-0 rounded-8 object-cover md:size-10 overflow-hidden',
  collisionBoundary,
  collisionLeft,
  disableElementDetail = false,
  enableAudioPlayback = false,
  enableAudioPreviewDialog = false,
}) => {
  const t = useTranslations();
  const media = useProcessedMedia(prompts, taskType);
  const [previewAudio, setPreviewAudio] = useState<ReferenceAudioPreviewInfo | null>(null);

  const { openReferenceModalWithData } = useStore((state) => ({
    openReferenceModalWithData: state.openReferenceModalWithData,
  }));

  if (!media || media.length === 0) return null;

  // 超过 MAX_DISPLAY_ITEMS 个媒体时，只显示第一个，后面的用叠加的方式显示
  const displayMedia = media.length <= MAX_DISPLAY_ITEMS ? media : [media[0]];
  const isStacked = media.length > MAX_DISPLAY_ITEMS;

  return (
    <>
      <CustomTooltip
        side="bottom"
        align="start"
        variant="extend"
        sideOffset={8}
        collisionBoundary={collisionBoundary}
        collisionLeft={collisionLeft}
        contentClassName="max-w-fit p-2 min-h-[100px] bg-[#22252A] rounded-12 border border-system-white24"
        trigger="auto"
        content={
          <div className="flex max-w-[724px] flex-wrap gap-1 lg:max-w-[516px]">
            {/* biome-ignore lint/suspicious/useIterableCallbackReturn: some media states intentionally render nothing */}
            {media.map((item, index) => {
              // 审核失败的情况，显示审核失败图标
              if (item.audit_violation) {
                return (
                  <ReferenceCard key={`${item.type}-${index}`} className="h-[100px] w-[100px]" footer={item.name}>
                    <div className="flex h-full w-full flex-col items-center justify-center rounded-8 bg-system-black">
                      <Fail className="size-5 text-system-white64" />
                      <div className="mt-1 text-center text-system-white/40 text-xs">
                        {t('popup.dialog.error.checkFailed.title')}
                      </div>
                    </div>
                  </ReferenceCard>
                );
              }

              if (
                (item.type === 'reference' || item.type === 'reference_text') &&
                (!item.material?.id || item.material.id === '0')
              ) {
                return (
                  <ReferenceCard
                    key={`${item.type}-${index}`}
                    className="h-[100px] w-[100px]"
                    footer={
                      <>
                        <ProductLineElement className="size-[10px] flex-shrink-0 text-system-white/40" />
                        <span className="ml-1 truncate text-xs">{item.name}</span>
                      </>
                    }
                  >
                    <div className="flex h-full w-full flex-col items-center justify-center bg-system-black">
                      <div className="p-1 text-center text-[9px] text-system-white36 leading-[1.2]">
                        {t('elements.elementForbidden')}
                      </div>
                    </div>
                  </ReferenceCard>
                );
              }

              if (item.type === 'reference' && item.material) {
                const isShare = item.material.share_meta?.status === 'approved';
                const isVideoModality = item.material.modality === 'video';
                const FooterIcon = isVideoModality ? ProductLineVideoReference : ProductLineElement;
                return (
                  <div
                    key={`${item.type}-${index}`}
                    className={cn(!disableElementDetail && 'cursor-pointer')}
                    onClick={() => {
                      if (disableElementDetail) {
                        return;
                      }
                      openReferenceModalWithData({
                        referenceModalOpen: true,
                        element: item.material,
                        referenceModalType: isShare ? 'share' : 'reference',
                        referenceModalMode: 'view',
                        referenceModalFrom: 'others',
                      });
                    }}
                  >
                    <ReferenceCard
                      className="h-[100px] w-[100px]"
                      footer={
                        <div className="flex items-center gap-0.5">
                          <FooterIcon
                            className={cn(
                              'size-[10px] flex-shrink-0',
                              isShare ? 'text-[#AB8DFF]' : 'text-system-blue02',
                            )}
                          />
                          <span className="max-w-[50px] truncate">{item.name}</span>
                        </div>
                      }
                    >
                      <MediaPromptPreview
                        prompt={item.prompt ?? null}
                        className="rounded-8"
                        onClick={() => {
                          if (disableElementDetail) {
                            return;
                          }
                          openReferenceModalWithData({
                            referenceModalOpen: true,
                            element: item.material,
                            referenceModalType: isShare ? 'share' : 'reference',
                            referenceModalMode: 'view',
                            referenceModalFrom: 'others',
                          });
                        }}
                      />
                    </ReferenceCard>
                  </div>
                );
              }

              if (item.type === 'reference_text' && item.material) {
                const isShare = item.material.share_meta?.status === 'approved';
                return (
                  <div
                    key={`${item.type}-${index}`}
                    className={cn(!disableElementDetail && 'cursor-pointer')}
                    onClick={() => {
                      if (disableElementDetail) {
                        return;
                      }
                      openReferenceModalWithData({
                        referenceModalOpen: true,
                        element: item.material,
                        referenceModalType: isShare ? 'share' : 'reference',
                        referenceModalMode: 'view',
                        referenceModalFrom: 'others',
                      });
                    }}
                  >
                    <ReferenceCard
                      className="h-[100px] w-[100px]"
                      footer={
                        <div className="flex items-center gap-0.5">
                          <ProductLineElement
                            className={cn(
                              'size-[10px] flex-shrink-0',
                              isShare ? 'text-[#AB8DFF]' : 'text-system-blue02',
                            )}
                          />
                          <span className="max-w-[50px] truncate">{item.name}</span>
                        </div>
                      }
                    >
                      <TextElementCover name={item.name} className="rounded-8 text-xs" />
                    </ReferenceCard>
                  </div>
                );
              }

              if (item.type === 'reference_single_img') {
                return (
                  <ReferenceCard key={`${item.type}-${index}`} className="h-[100px] w-[100px]" footer={item.name}>
                    <MediaWithMaskBg src={item.src} maskSrc={item.src} className="rounded-8" />
                  </ReferenceCard>
                );
              }

              if (item.type === 'video') {
                return (
                  <ReferenceCard key={`${item.type}-${index}`} className="h-[100px] w-[100px]" footer={item.name}>
                    <TooltipVideo src={item.src} cover={item.cover} />
                  </ReferenceCard>
                );
              }

              if (item.type === 'audio') {
                const audioCard = (
                  <ReferenceCard key={`${item.type}-${index}`} className="h-[100px] w-[100px]" footer={item.name}>
                    <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-8 bg-system-black p-2">
                      <BaseLineMusic className="size-5 text-system-white64" />
                      {enableAudioPlayback && item.src ? (
                        <audio className="h-7 w-full" controls src={item.src}>
                          <track kind="captions" />
                        </audio>
                      ) : null}
                    </div>
                  </ReferenceCard>
                );

                if (enableAudioPreviewDialog && item.src) {
                  return (
                    <button
                      key={`${item.type}-${index}`}
                      type="button"
                      className="cursor-pointer text-left"
                      onClick={() => setPreviewAudio({ id: `${item.type}-${index}`, name: item.name, src: item.src })}
                    >
                      {audioCard}
                    </button>
                  );
                }

                return audioCard;
              }

              if (item.type === 'single') {
                return (
                  <ReferenceCard key={`${item.type}-${index}`} className="h-[100px] w-[100px]" footer={item.name}>
                    <MediaWithMaskBg src={item.src} maskSrc={item.src} className="rounded-8" />
                  </ReferenceCard>
                );
              }
            })}
          </div>
        }
      >
        <div className={cn('relative flex w-fit items-center', className, isStacked && 'mr-6')}>
          {isStacked && (
            <>
              <div
                className={cn(
                  thumbnailClassName,
                  '-right-2 absolute top-0 h-full w-[3px] rounded-l-none bg-white opacity-60',
                )}
              />
              <div
                className={cn(
                  thumbnailClassName,
                  '-right-1 absolute top-0 h-full w-[3px] rounded-l-none bg-white opacity-80',
                )}
              />
              <div className="absolute top-[-6px] right-[-14px] z-10 flex size-6 items-center justify-center rounded-full bg-white/16 font-medium text-white text-xs shadow-lg backdrop-blur-[5px]">
                {media.length}
              </div>
            </>
          )}

          {displayMedia.map((item, idx) => (
            <Fragment key={`${item.type}-${item.name || idx}`}>
              <div className={thumbnailClassName}>
                {item.audit_violation ? (
                  <div className="flex h-full w-full flex-col items-center justify-center rounded-2 bg-system-black">
                    <Fail className="size-5 text-system-white64" />
                  </div>
                ) : (item.type === 'reference' || item.type === 'reference_text') &&
                  (!item.material?.id || item.material.id === '0') ? (
                  <div className="flex h-full w-full flex-col items-center justify-center rounded-2 bg-system-black">
                    <BaseLineExclamation className="size-5 text-system-white64" />
                  </div>
                ) : item.type === 'video' ? (
                  <ThumbnailVideo src={item.src} cover={item.cover} />
                ) : item.type === 'audio' ? (
                  <div className="flex h-full w-full items-center justify-center rounded-2 bg-system-black">
                    <BaseLineMusic className="size-5 text-system-white64" />
                  </div>
                ) : item.type === 'reference' ? (
                  <MediaPromptPreview prompt={item.prompt ?? null} className="rounded-2" />
                ) : item.type === 'reference_text' ? (
                  <TextElementCover name={item.name} className="rounded-2 px-1 py-0 text-xs" />
                ) : (
                  <MediaWithMaskBg src={item.src} maskSrc={item.src} className="rounded-2" />
                )}
              </div>

              {idx < displayMedia.length - 1 &&
                (taskType === 'headtailimg2video' ? (
                  <ProductFillFrames className="-mx-1 relative z-10 text-base" />
                ) : (
                  <ProductFillMultiagent className="-mx-1 relative z-10 text-base" />
                ))}
            </Fragment>
          ))}
        </div>
      </CustomTooltip>
      <ReferenceAudioPreviewDialog audio={previewAudio} onOpenChange={(open) => !open && setPreviewAudio(null)} />
    </>
  );
};
